import { useMemo } from 'react';
import Badge from '@/components/base/Badge';
import Button from '@/components/base/Button';
import Modal from '@/components/base/Modal';
import { formatMoney } from '@/utils/format';
import { isInlineFixableIssue, summarizeRows } from '@/utils/bulkPayments';
import type { BulkChannel, BulkRecipient } from '@/types/portal';

interface ValidationReportModalProps {
  label: string;
  backLabel: string;
  channel: BulkChannel;
  currency: string;
  rows: BulkRecipient[];
  onFixRow: (id: string, patch: Partial<BulkRecipient>) => void;
  onRemoveFlagged: () => void;
  onClose: () => void;
  onContinue: () => void;
}

export default function ValidationReportModal({
  label,
  backLabel,
  channel,
  currency,
  rows,
  onFixRow,
  onRemoveFlagged,
  onClose,
  onContinue,
}: ValidationReportModalProps) {
  const summary = useMemo(() => summarizeRows(rows, channel), [rows, channel]);
  const { total, ready, flagged } = summary;
  const rowById = useMemo(() => new Map(rows.map((row) => [row.id, row])), [rows]);
  const fixableCount = flagged.filter((flag) => isInlineFixableIssue(flag.reason)).length;

  return (
    <Modal
      open
      onClose={onClose}
      icon="ri-shield-check-line"
      title="Validation report"
      subtitle={`We checked ${total} row${total === 1 ? '' : 's'} in ${label}.`}
      widthClassName="max-w-2xl"
      footer={
        <>
          <Button variant="outline" icon="ri-arrow-left-line" onClick={onClose}>
            {backLabel}
          </Button>
          <Button icon="ri-arrow-right-line" onClick={onContinue} disabled={ready === 0}>
            Continue to Review
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        <div className="grid grid-cols-2 gap-3">
          <div className="rounded-lg border border-accent-200 bg-accent-50 px-4 py-3">
            <p className="text-xs font-label text-accent-800">Ready to send</p>
            <p className="mt-1 font-heading text-2xl font-semibold text-accent-900">{ready}</p>
          </div>
          <div className="rounded-lg border border-background-200 bg-background-100 px-4 py-3">
            <p className="text-xs font-label text-foreground-500">Flagged</p>
            <p className="mt-1 font-heading text-2xl font-semibold text-foreground-950">
              {flagged.length}
            </p>
          </div>
        </div>

        {flagged.length === 0 ? (
          total > 0 ? (
            <div className="flex items-center gap-2 rounded-lg border border-accent-200 bg-accent-50 px-4 py-3 text-sm text-accent-900">
              <i className="ri-checkbox-circle-line text-base" />
              Every row looks good — nothing to fix.
            </div>
          ) : (
            <div className="flex items-center gap-2 rounded-lg border border-background-200 bg-background-100 px-4 py-3 text-sm text-foreground-600">
              <i className="ri-information-line text-base" />
              No rows left — add recipients to continue.
            </div>
          )
        ) : (
          <div className="overflow-hidden rounded-lg border border-background-200">
            <header className="flex flex-wrap items-center justify-between gap-2 border-b border-background-200 bg-background-100 px-4 py-2.5">
              <span className="text-xs font-label font-semibold uppercase tracking-wide text-foreground-500">
                Lines that need attention
              </span>
              <div className="flex items-center gap-2">
                <Button variant="ghost" size="sm" icon="ri-delete-bin-6-line" onClick={onRemoveFlagged}>
                  Remove all flagged
                </Button>
                <Badge tone="primary" icon="ri-error-warning-line">
                  {flagged.length} flagged
                </Badge>
              </div>
            </header>

            {fixableCount > 0 ? (
              <div className="flex items-center gap-2 border-b border-background-200 bg-primary-50 px-4 py-2 text-xs text-primary-800">
                <i className="ri-magic-line text-sm" />
                Fix {fixableCount} here — the count updates as rows become valid.
              </div>
            ) : null}

            <ul className="divide-y divide-background-100">
              {flagged.map((flag) => {
                const row = rowById.get(flag.id);
                const fixable = isInlineFixableIssue(flag.reason);
                return (
                  <li key={flag.id} className="px-4 py-3">
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0">
                        <p className="truncate text-sm font-medium text-foreground-950">{flag.name}</p>
                        <p className="truncate text-xs text-foreground-500">{flag.target}</p>
                      </div>
                      <span className="shrink-0 text-sm text-foreground-700">
                        {flag.amount > 0 ? formatMoney(flag.amount, currency) : '\u2014'}
                      </span>
                    </div>

                    <p
                      className={`mt-1.5 inline-flex items-center gap-1.5 text-xs ${
                        fixable ? 'text-primary-800' : 'text-accent-800'
                      }`}
                    >
                      <i className={fixable ? 'ri-error-warning-line' : 'ri-lock-line'} />
                      {flag.reason}
                    </p>

                    {fixable && row ? (
                      <div className="mt-2 grid grid-cols-1 gap-2 sm:grid-cols-2">
                        <label className="block">
                          <span className="text-[11px] font-label text-foreground-500">
                            {channel === 'mobile' ? 'Mobile number' : 'Wallet ID'}
                          </span>
                          <input
                            value={row.walletId}
                            onChange={(event) => onFixRow(row.id, { walletId: event.target.value })}
                            placeholder={channel === 'mobile' ? 'e.g. 254712345678' : 'e.g. 2314'}
                            className="mt-1 h-9 w-full rounded-md border border-background-300 bg-background-50 px-2.5 text-sm text-foreground-800 placeholder:text-foreground-400 focus:border-primary-400 focus:outline-none"
                          />
                        </label>
                        <label className="block">
                          <span className="text-[11px] font-label text-foreground-500">
                            Amount ({currency})
                          </span>
                          <input
                            type="number"
                            min={0}
                            value={row.amount || ''}
                            onChange={(event) => onFixRow(row.id, { amount: Number(event.target.value) })}
                            placeholder="e.g. 5000"
                            className="mt-1 h-9 w-full rounded-md border border-background-300 bg-background-50 px-2.5 text-sm text-foreground-800 placeholder:text-foreground-400 focus:border-primary-400 focus:outline-none"
                          />
                        </label>
                      </div>
                    ) : (
                      <p className="mt-1.5 text-xs text-foreground-500">
                        Can't fix this here — resolve it on the recipient's record, then reload.
                      </p>
                    )}
                  </li>
                );
              })}
            </ul>
          </div>
        )}

        <p className="text-xs text-foreground-500">
          Flagged lines are excluded from the batch. Fix a missing{' '}
          {channel === 'mobile' ? 'mobile number' : 'wallet ID'} or amount and the row moves to Ready to send.
        </p>
      </div>
    </Modal>
  );
}